import { type FC, useEffect } from 'react';

import { httpGetCollectionsByCollectionId } from '@iconik-io/iconik-api/src/services/assets';

import { getEntityCurrentAcls } from '../../ducks/common_acls/thunks';
import {
  selectCanUploadByPermissions,
  selectIfCanChooseUploadStorage,
  selectStorageOptionDisabled,
  selectUploadStorage,
} from '../../panels/uploads/selectors';
import { useLeafRouteParams } from '../../router/use_leaf_route_params';
import { useAppDispatch, useAppSelector } from '../../store/react_redux_typed';
import { SelectUploadStorageFieldWrapper } from '../../ui/forms/field_wrappers/select_upload_storage/select_upload_storage';
import TextFieldWrapper from '../../ui/forms/field_wrappers/text/text';
import type { ShareSettingFieldProps } from './types';

type Props = {
  fieldProps: ShareSettingFieldProps;
  objectId: string;
  disabled?: boolean;
};

const UploadSetting: FC<Props> = ({ fieldProps, objectId, disabled }) => {
  const { route } = useLeafRouteParams();
  const dispatch = useAppDispatch();
  const storageId = useAppSelector((state) =>
    selectUploadStorage(state, objectId, undefined, route),
  );
  const canUpload = useAppSelector((state) =>
    selectCanUploadByPermissions(state, objectId),
  );
  const optionsDisabled = useAppSelector((state) =>
    selectStorageOptionDisabled(state, objectId),
  );
  const collections = useAppSelector((state) => state.entities.collections);

  const canChooseStorage = useAppSelector(selectIfCanChooseUploadStorage);

  useEffect(() => {
    if (!collections[objectId]) {
      dispatch(getEntityCurrentAcls('collections', objectId));
      dispatch(httpGetCollectionsByCollectionId(objectId));
    }
  }, [objectId, collections[objectId]]);

  const displayDropdown =
    collections[objectId] && canUpload && canChooseStorage && !optionsDisabled;
  const displayHiddenInput =
    collections[objectId] &&
    canUpload &&
    (!canChooseStorage || optionsDisabled);

  return (
    <>
      {displayHiddenInput ? (
        <TextFieldWrapper
          type="hidden"
          value={storageId}
          name="share.upload_storage_id"
        />
      ) : undefined}
      {displayDropdown ? (
        <SelectUploadStorageFieldWrapper
          {...fieldProps.upload_storage_id}
          value={fieldProps.upload_storage_id?.value ?? storageId}
          canClear={false}
          disabled={disabled}
          label="Upload Storage"
          selectFirstAvailableOption
          name="share.upload_storage_id"
          key="upload_storage_id"
          isMulti={false}
        />
      ) : undefined}
    </>
  );
};

export default UploadSetting;
